import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEC_ApiCall } from '../func.js';
import LoadingComp from '../common/loading.js';
import '../alert/Alert.css';
import './AppSupport.css';

/**
 * Landing view for App Support requests using the Alert Centre layout styling.
 */
export default function AppSupportHome() {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);

    // Navigate to the new App Support request form.
    const handleCreateRequest = () => {
        navigate('/app/support/requests/new');
    };

    const fetchSupportRequests = useCallback(async () => {
        setApiError(false);
        try {
            const response = await SEC_ApiCall('appsupport/user', {}, setApiError);
            const parsed = response?.body ? safeParse(response.body) : response;
            const items = parsed?.requests;
            if (Array.isArray(items)) {
                setRequests(items);
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error('Failed to load App Support requests', error);
            setApiError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSupportRequests();
    }, [fetchSupportRequests]);

    const currentRequests = useMemo(() => {
        if (!Array.isArray(requests)) {
            return [];
        }
        return requests;
    }, [requests]);

    return (
        <div className="alert-home-container">
            <div className="appsupport-top-actions">
                <button
                    type="button"
                    className="appsupport-return-button"
                    onClick={() => navigate('/app/support')}
                >
                    Return to Support Home
                </button>
            </div>
            <div className="alert-home-header">
                <div>
                    <h1 className="alert-home-heading" style={{ textAlign: 'left' }}>App Support</h1>
                    <p className="alert-home-body alert-home-subhead" style={{ textAlign: 'left' }}>
                        Log and track support requests related to School Engine.
                    </p>
                </div>
                <button
                    type="button"
                    className="alert-create-button"
                    onClick={handleCreateRequest}
                >
                    Create New Request
                </button>
            </div>

            {apiError && (
                <p className="alert-home-body alert-home-error" role="alert">
                    We could not load your App Support requests right now. Please try again shortly.
                </p>
            )}

            <div className="alert-home-card appsupport-current-card">
                <div className="alert-home-header appsupport-current-header">
                    <div>
                        <h2 className="alert-home-heading" style={{ textAlign: 'center' }}>Current Requests</h2>
                        <p className="alert-home-body alert-home-subhead" style={{ textAlign: 'center' }}>
                            Your active support conversations will show here.
                        </p>
                    </div>
                </div>
                {loading && (
                    <div className="alert-active-empty appsupport-current-empty">
                        <LoadingComp />
                    </div>
                )}
                {!loading && currentRequests.length === 0 && (
                    <div className="alert-active-empty appsupport-current-empty">
                        <p className="alert-active-empty-text">No current requests</p>
                    </div>
                )}
                {!loading && currentRequests.length > 0 && (
                    <div className="appsupport-request-grid">
                        {currentRequests.map((request) => {
                            const caseId = request.caseId || request.requestId || 'Pending';
                            return (
                                <div
                                    key={request.requestId || request.caseId || request.createdAt}
                                    className="appsupport-request-card"
                                >
                                    <div className="appsupport-request-header">
                                        <p className="appsupport-request-type">
                                            {request.requestType || 'Support request'}
                                        </p>
                                        <span className="appsupport-request-case">Case {caseId}</span>
                                    </div>
                                    <h3 className="appsupport-request-title">
                                        {request.requestTitle || 'Untitled request'}
                                    </h3>
                                    <div className="appsupport-request-meta">
                                        <span className="appsupport-request-status">
                                            {request.status || 'New'}
                                        </span>
                                        <span className="appsupport-request-date">
                                            {formatDate(request.createdAt)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function safeParse(value) {
    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
}

function formatDate(rawValue) {
    if (!rawValue) {
        return 'Date pending';
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) {
        return rawValue;
    }

    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(parsed);
}
